import React, { useMemo } from 'react';
import { Typography } from '@material-ui/core';
import { RMSDataGrid } from '../../../shared/components/table';
import RMSChip from '../../../shared/components/chips/RMSChip';
import DATE_FORMATS from '../../../shared/constants/dateFormats';
import { formatDate } from '../../../shared/utility/dateFormatter';
import usePortalCheckoutRequestsAll from '../../hooks/usePortalCheckoutRequestsAll';
import {
  PORTAL_CHECKOUT_CLOSED_STATUS_CODES,
  PORTAL_CHECKOUT_TABLE_COLUMN_LABELS,
} from '../../constants/evidencePortalConstants';
import { getStatusColor } from '../../constants/checkOutRequestTab';

const getItemLabel = (row) => row.incEvidence?.EvidenceID || `#${row.ptsEvidenceID}`;

const EvidencePortalCheckoutRequestsTable = ({ onOpenEvidence }) => {
  const { allCheckoutRequests } = usePortalCheckoutRequestsAll();

  const { activeRequests, pastRequests } = useMemo(() => {
    const active = [];
    const past = [];
    allCheckoutRequests.forEach((item) => {
      if (PORTAL_CHECKOUT_CLOSED_STATUS_CODES.includes(item.StatusDetails?.Code)) past.push(item);
      else active.push(item);
    });
    return { activeRequests: active, pastRequests: past };
  }, [allCheckoutRequests]);

  const activeColumns = useMemo(
    () => [
      {
        field: 'Item',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.item,
        render: (_, row) => (
          <span className="evidence-portal-table__eid">{getItemLabel(row)}</span>
        ),
      },
      {
        field: 'Reason',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.reason,
        render: (_, row) => row.ReasonNeeded?.Code || '—',
      },
      {
        field: 'Requested',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.requested,
        render: (_, row) => formatDate(row.RequestDateTime, DATE_FORMATS.MM_DD_YYYY_HH_mm_A) || '—',
      },
      {
        field: 'Pickup',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.pickup,
        render: (_, row) => formatDate(row.PickupDate, DATE_FORMATS.MM_DD_YYYY_HH_mm_A) || '—',
      },
      {
        field: 'Status',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.status,
        render: (_, row) => (
          <RMSChip
            label={row.StatusDetails?.Code || 'Pending'}
            size="small"
            color={getStatusColor(row.StatusDetails?.Code)}
          />
        ),
      },
      {
        field: 'Comments',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.comments,
        render: (_, row) => row.Comments || '',
      },
    ],
    []
  );

  const pastColumns = useMemo(
    () => [
      {
        field: 'Item',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.item,
        render: (_, row) => (
          <span className="evidence-portal-table__eid">{getItemLabel(row)}</span>
        ),
      },
      {
        field: 'Reason',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.reason,
        render: (_, row) => row.ReasonNeeded?.Code || '—',
      },
      {
        field: 'Requested',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.requested,
        render: (_, row) => formatDate(row.RequestDateTime, DATE_FORMATS.MM_DD_YYYY_HH_mm_A) || '—',
      },
      {
        field: 'Status',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.status,
        render: (_, row) => (
          <RMSChip
            label={row.StatusDetails?.Code || '—'}
            size="small"
            color={getStatusColor(row.StatusDetails?.Code)}
          />
        ),
      },
      {
        field: 'ProcessedBy',
        title: PORTAL_CHECKOUT_TABLE_COLUMN_LABELS.processedBy,
        render: (_, row) => row.ProcessedByPerson?.FullName || '—',
      },
    ],
    []
  );

  return (
    <>
      <Typography variant="subtitle2" className="evidence-portal-table-title">
        Active Requests
      </Typography>
      {!activeRequests.length ? (
        <div className="evidence-portal-empty">No active checkout requests.</div>
      ) : (
        <div className="evidence-portal-table-wrapper">
          <RMSDataGrid
            primaryKey="ptsEvidenceRequestID"
            columns={activeColumns}
            rows={activeRequests}
            onRowClick={(row) => onOpenEvidence(row.ptsEvidenceID)}
          />
        </div>
      )}

      {pastRequests.length > 0 && (
        <>
          <Typography variant="subtitle2" className="evidence-portal-table-title is-muted">
            Past Requests
          </Typography>
          <div className="evidence-portal-table-wrapper is-muted">
            <RMSDataGrid
              primaryKey="ptsEvidenceRequestID"
              columns={pastColumns}
              rows={pastRequests}
              onRowClick={(row) => onOpenEvidence(row.ptsEvidenceID)}
            />
          </div>
        </>
      )}
    </>
  );
};

export default EvidencePortalCheckoutRequestsTable;
